import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { env } from './env';

export const supabase = createClient<Database>(
  env.supabase.url,
  env.supabase.anonKey
);

// Helper functions for common Supabase operations
export async function getUserProfile(phoneNumber: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('phone_number', phoneNumber)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching user profile:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    if ((error as any)?.code !== 'PGRST116') {
      console.error('Error fetching user profile:', error);
      throw error;
    }
    return null;
  }
}

// New function to check if user exists by phone number
export async function checkUserExists(phoneNumber: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('phone_number')
      .eq('phone_number', phoneNumber)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return false; // User doesn't exist
      }
      throw error;
    }
    
    return !!data; // User exists
  } catch (error) {
    console.error('Error checking user existence:', error);
    return false;
  }
}

// New function to create minimal user profile for new users
export async function createMinimalUserProfile(phoneNumber: string) {
  const { data, error } = await supabase
    .from('profiles')
    .insert([{
      phone_number: phoneNumber,
      name: '', // Empty - will trigger profile completion
      gender: 'male',
      email: '',
      birth_date: '', // Empty - will trigger profile completion
      birth_time: '', // Empty - will trigger profile completion
      birth_location: {
        city: '', // Empty - will trigger profile completion
        country: 'India'
      },
      has_used_free_reading: false, // New users get a free reading
      shares_available: 0,
      is_premium: false
    }])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating minimal user profile:', error);
    throw error;
  }
  
  return data;
}

export async function updateUserProfile(phoneNumber: string, userData: Partial<UserProfile>) {
  const { data, error } = await supabase
    .from('profiles')
    .upsert({ 
      ...userData, 
      phone_number: phoneNumber 
    }, {
      onConflict: 'phone_number'
    })
    .select()
    .single();
  
  if (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
  
  return data;
}

export async function createUserProfile(userData: Partial<UserProfile>) {
  const { data, error } = await supabase
    .from('profiles')
    .insert([userData])
    .select()
    .single();
  
  if (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
  
  return data;
}

export async function saveReading(readingData: Partial<Reading>) {
  try {
    const { data, error } = await supabase.rpc(
      'save_reading_with_phone',
      {
        p_user_phone: readingData.user_phone,
        p_reading_type: readingData.reading_type,
        p_prediction_text: readingData.prediction_text
      }
    );

    if (error) {
      console.error('Error saving reading:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error saving reading:', error);
    throw error;
  }
}

// Updated function to create a reading using the new free reading system
export async function createReadingWithFreeAccess(readingData: Partial<Reading>) {
  try {
    console.log('Creating reading with free access system');
    const { data, error } = await supabase.rpc(
      'create_free_reading',
      {
        p_user_phone: readingData.user_phone,
        p_reading_type: readingData.reading_type,
        p_prediction_text: readingData.prediction_text
      }
    );

    if (error) {
      console.error('Error creating reading with free access:', error);
      throw error;
    }
    
    console.log('Reading created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error creating reading with free access:', error);
    throw error;
  }
}

// Backward compatibility function
export async function createReadingWithShare(readingData: Partial<Reading>) {
  return createReadingWithFreeAccess(readingData);
}

// Function to add a share when user shares the app
export async function addUserShare(phoneNumber: string) {
  try {
    console.log('Adding share for user:', phoneNumber);
    const { data, error } = await supabase.rpc(
      'add_user_share',
      {
        p_user_phone: phoneNumber
      }
    );

    if (error) {
      console.error('Error adding user share:', error);
      throw error;
    }
    
    console.log('Share added successfully');
    return data;
  } catch (error) {
    console.error('Error adding user share:', error);
    throw error;
  }
}

export async function getUserReadings(phoneNumber: string) {
  const { data, error } = await supabase
    .from('readings')
    .select('*')
    .eq('user_phone', phoneNumber)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching user readings:', error);
    throw error;
  }
  
  return data;
}

export interface UserProfile {
  id?: string;
  phone_number: string;
  name: string;
  gender: string;
  email?: string;
  birth_date: string;
  birth_time: string;
  birth_location: {
    city: string;
    state?: string;
    country: string;
  };
  is_premium?: boolean;
  shares_available?: number;
  has_used_free_reading?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Reading {
  reading_id?: string;
  user_phone: string;
  reading_type: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'career' | 'love' | 'health' | 'life';
  prediction_text: string;
  created_at?: string;
}